import "./index.scss";

import { Box, Stack } from "@mui/material";
import DatePicker from "components/DatePicker";
import Input from "components/Input";
import { DATE_FORMAT } from "constants/index";
import { TPGPLongTermGoal } from "models/tca";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import ReactToPrint from "react-to-print";

import YmcaPageHeader from "../../SMS/components/YmcaPageHeader";

const ContractPreview = (props: {
  goals: TPGPLongTermGoal[];
  contractWorkoutSessions: number;
  contractNumberOfWeeks: number;
  sendContractToBackend: () => {};
}) => {
  const { goals, contractWorkoutSessions, contractNumberOfWeeks, sendContractToBackend } = props;
  const [agreedNextAppointmentDate, setAgreedNextAppointmentDate] = useState("");
  const [memberSignature, setMemberSignature] = useState("");
  const [wellnessCoachSignature, setWellnessCoachSignature] = useState("");
  const printTemplateRef = useRef();
  const today = new Date();
  const formContext = useFormContext();
  const { getValues, setValue } = formContext;

  useEffect(() => {
    setAgreedNextAppointmentDate(moment(getValues("nextAppointmentDate")).format(DATE_FORMAT));
  }, []);

  return (
    <>
      <Stack className="greenContainer contractPreview tcaSms">
        <Stack className="containerPadding" flexGrow={1}>
          <Box className="header1 orange" textAlign={"center"} my={2}>
            Exercise Adherence and Healthy Lifestyles Contract
          </Box>
          <Stack direction={"row"} className="text text-bold" alignItems={"center"} gap={2}>
            <Box>
              An important component of adherence is clearly outlining expectations, i.e., long and
              short-term goals, between now,
            </Box>
            <DatePicker className="base-dropdown" value={today} disabled onChange={() => void 0} />
          </Stack>
          <Stack direction={"row"} className="text text-bold" alignItems={"center"} gap={2}>
            <Box>and our next Coach Approach appointment around,</Box>
            <DatePicker
              className="base-dropdown"
              value={moment(agreedNextAppointmentDate).format(DATE_FORMAT)}
              minDate={today}
              onChange={(v) => setAgreedNextAppointmentDate(moment(v).format(DATE_FORMAT))}
            />
          </Stack>
          <Box className="text text-bold" my={2}>
            As a team, we have identified your long-term goals and have, together, broken these
            long-term goals down into some short-term process and/or outcome goals, which are as
            follows:
          </Box>
          <Box>
            {goals?.map((m, mIndex) => {
              if (!m.isSelectedInContract) return null;
              return (
                <Stack className="whiteBox" mb={"20px"} key={`previewm${mIndex}`}>
                  <Box className="goalCategory">Long-term goal:</Box>
                  <Box className="text text-bold">{m.longTermGoal}</Box>
                  {m.shortTermGoals?.filter((f) => f.isSelectedInContract).length > 0 && (
                    <Stack mt={2.5}>
                      <Box className="goalCategory">Short-term goal:</Box>
                      {m.shortTermGoals.map((mm, mmIndex) => {
                        if (!mm.isSelectedInContract) return null;
                        return (
                          <Box className="text text-bold" key={`previewmm${mmIndex}`}>
                            {mm.whereIWantToBe}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Box>
          <Box className="text text-bold" mb={2}>
            If we have accurately captured your long and short-term goals that will be your focus
            between now and our next appointment, please sign below.
          </Box>
          <Box className="text text-bold" mb={2}>
            In addition to these goals, my wellness coach and I have discussed my exercise plan and
            I agree that I will commit to completing {contractWorkoutSessions} sessions through the
            next {contractNumberOfWeeks} weeks between now,
          </Box>
          <Stack direction={"row"} className="text text-bold" alignItems={"center"} gap={2}>
            <DatePicker className="base-dropdown" value={today} disabled onChange={() => void 0} />
            <Box>and our next Coach Approach appointment around,</Box>
            <DatePicker
              className="base-dropdown"
              value={moment(agreedNextAppointmentDate).format(DATE_FORMAT)}
              minDate={today}
              onChange={(v) => setAgreedNextAppointmentDate(moment(v).format(DATE_FORMAT))}
            />
          </Stack>
          <Stack direction={"row"} flexGrow={1} my={2.5} gap={4}>
            <Stack flexBasis={0} flexGrow={1} gap={2}>
              <Box className="text text-bold">Member's Signature:</Box>
              <Input value={memberSignature} onChange={(v) => setMemberSignature(v.target.value)} />
            </Stack>
            <Stack flexBasis={0} flexGrow={1} gap={2}>
              <Box className="text text-bold">Wellness Coach Signature:</Box>
              <Input
                value={wellnessCoachSignature}
                onChange={(v) => setWellnessCoachSignature(v.target.value)}
              />
            </Stack>
          </Stack>
          <Box>
            <hr className="containerDivider" />
          </Box>
          <Stack direction={"row"} justifyContent="space-around">
            <button type="button" className="action-button" onClick={() => sendContractToBackend()}>
              Email Document to the Member
            </button>
            <ReactToPrint
              trigger={() => (
                <button type="button" className="action-button">
                  Print Document
                </button>
              )}
              content={() => printTemplateRef.current}
            />
          </Stack>
        </Stack>
      </Stack>
      <div className="out-of-sight">
        <Stack ref={printTemplateRef} className="elementToPrint">
          <YmcaPageHeader />
          <Box className="contractTitle" textAlign={"center"}>
            EXERCISE ADHERENCE AND HEALTHY LIFESTYLES CONTRACT
          </Box>
          <Box mt={4}>
            <p>
              An important component of adherence is clearly outlining expectations, i.e., long and
              short-term goals, between now,{" "}
              <span className="underlinedPlaceholders">{moment(today).format(DATE_FORMAT)}</span>{" "}
              and our next Coach Approach appointment around,{" "}
              <span className="underlinedPlaceholders">{agreedNextAppointmentDate}</span>.
            </p>
          </Box>
          <Box mt={2}>
            <p>
              As a team, we have identified your long-term goals and have, together, broken these
              long-term goals down into some short-term process and/or outcome goals, which are as
              follows:
            </p>
          </Box>
          <Box mt={2.5}>
            {goals?.map((m, mIndex) => {
              if (!m.isSelectedInContract) return null;
              return (
                <Stack mb={3} key={`printm${mIndex}`}>
                  <Stack direction={"row"} gap={1}>
                    <Box>
                      <p>Long-term goal:</p>
                    </Box>
                    <Box className="underlinedDiv" flexGrow={1}>
                      {m.longTermGoal}
                    </Box>
                  </Stack>
                  {m.shortTermGoals?.filter((f) => f.isSelectedInContract).length > 0 && (
                    <Stack gap={1}>
                      <Box>
                        <p>Short-term goal:</p>
                      </Box>
                      <ul>
                        {m.shortTermGoals.map((mm, mmIndex) => {
                          if (!mm.isSelectedInContract) return null;
                          return <li>{mm.whereIWantToBe}</li>;
                        })}
                      </ul>
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Box>
          <Box mt={2}>
            <p>
              If we have accurately captured your long and short-term goals that will be your focus
              between now and our next appointment, please sign below.
            </p>
          </Box>
          <Box mt={2}>
            <p>
              In addition to these goals, my wellness coach and I have discussed my exercise plan
              and I agree that I will commit to completing {contractWorkoutSessions} sessions
              through the next {contractNumberOfWeeks} weeks between now,{" "}
              <span className="underlinedPlaceholders">{moment(today).format(DATE_FORMAT)}</span>{" "}
              and our next Coach Approach appointment around,{" "}
              <span className="underlinedPlaceholders">{agreedNextAppointmentDate}</span>.
            </p>
          </Box>
          <Stack direction={"row"} gap={1} mt={2}>
            <Box>
              <p>Member's Signature:</p>
            </Box>
            <Box className="underlinedDiv" flexGrow={1}>
              {memberSignature}
            </Box>
          </Stack>
          <Stack direction={"row"} gap={1}>
            <Box>
              <p>Wellness Coach Signature:</p>
            </Box>
            <Box className="underlinedDiv" flexGrow={1}>
              {wellnessCoachSignature}
            </Box>
          </Stack>
        </Stack>
      </div>
    </>
  );
};

export default ContractPreview;
